"use client";

import { MediaImage } from "@/shared/components/MediaImage";
import { formatBRL } from "@/shared/helpers";
import { Button } from "@dionatanbig2be/big2be-ui";

interface SearchProductPreview {
    name: string;
    brand?: string;
    imageUrl?: string;
    price?: number;
    oldPrice?: number;
    badgeLabel?: string;
}

interface SearchProductCardProps<T extends SearchProductPreview> {
    product: T;
    onClick?: (product: T) => void;
    fullWidth?: boolean;
}

/** Card compacto de produto visitado, exibido na vitrine do dropdown de busca. */
export function SearchProductCard<T extends SearchProductPreview>({
    product,
    onClick,
    fullWidth = false,
}: SearchProductCardProps<T>) {
    const { name, brand, imageUrl, price, oldPrice, badgeLabel } = product;

    return (
        <Button
            type="button"
            onClick={() => onClick?.(product)}
            className={`flex h-auto min-h-0 items-stretch overflow-hidden rounded-xl border border-base-300 bg-base-100 p-0 text-left transition-shadow hover:shadow-md ${fullWidth ? "w-full" : "w-[320px] min-w-[320px]"}`}
        >
            <div className={`relative m-auto shrink-0 p-2 ${fullWidth ? "size-20" : "size-[104px]"}`}>
                {imageUrl && (
                    <MediaImage
                        source="products"
                        path={imageUrl}
                        alt={name}
                        fill
                        sizes={fullWidth ? "80px" : "104px"}
                        className="rounded-xl object-contain p-1"
                    />
                )}
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-0.5 p-2">
                {badgeLabel && (
                    <span className="badge badge-xs w-fit gap-1 border-none bg-primary/15 font-medium text-primary">
                        {badgeLabel}
                    </span>
                )}
                {brand && (
                    <span className="truncate text-[10px] leading-tight text-base-content/50">{brand}</span>
                )}
                <span className="line-clamp-2 text-xs font-medium leading-tight text-base-content">{name}</span>

                <div className="mt-auto pt-0.5">
                    {oldPrice && (
                        <span className="block text-[10px] leading-none text-base-content/40 line-through">
                            {formatBRL(oldPrice)}
                        </span>
                    )}
                    {typeof price === "number" && (
                        <span className="text-sm font-bold text-base-content">{formatBRL(price)}</span>
                    )}
                </div>
            </div>
        </Button>
    );
}
